"use client"

import React from "react"
import {
  Calendar,
  Star,
  Users,
  Download,
  Share2,
  Heart,
  Info,
  MapPin,
  DollarSign,
  Utensils,
  Hotel,
  Car,
  Camera,
  ShoppingBag,
} from "lucide-react"

interface TravelPlanDisplayProps {
  suggestions: string
  tripData?: {
    duration: number
    travelers: number
    locations: string[]
    startDate: string
    endDate: string
  }
}

// Location Pin Icon Component
const LocationPinIcon: React.FC<{ className?: string }> = ({ className = "h-3 w-3" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
  </svg>
)

interface ItineraryData {
  itinerary: {
    title: string
    duration: number
    travelers: number
    locations: string[]
    days: DayData[]
    budget: BudgetData
    essentials: EssentialsData
  }
}

interface DayData {
  dayNumber: number
  date: string
  title: string
  city: string
  schedule: ScheduleItem[]
  meals: MealData[]
  accommodation: AccommodationData
}

interface ScheduleItem {
  timeSlot: string
  period: string
  activities: ActivityData[]
}

interface ActivityData {
  time: string
  activity: string
  location: string
  type: string
  cost: string
  duration: string
  tips: string
}

interface MealData {
  type: string
  restaurant: string
  location: string
  cuisine: string
  cost: string
  speciality: string
  timing: string
}

interface AccommodationData {
  name: string
  location: string
  type: string
  cost: string
  rating: string
  amenities: string[]
}

interface BudgetData {
  accommodation: string
  food: string
  transport: string
  attractions: string
  total: string
}

interface EssentialsData {
  bestTime: string
  weather: string
  clothing: string
  language: string
}

const TravelPlanDisplay: React.FC<TravelPlanDisplayProps> = ({ suggestions, tripData }) => {
  // Function to clean text and remove markdown formatting
  const cleanText = (text: string): string => {
    if (!text) return ""
    return text
      .replace(/\*\*/g, "") // Remove bold markdown
      .replace(/\*/g, "") // Remove italic markdown
      .replace(/#{1,6}\s/g, "") // Remove heading markdown
      .replace(/\[([^\]]+)\]$$[^)]+$$/g, "$1") // Convert links to plain text
      .replace(/`([^`]+)`/g, "$1") // Remove code formatting
      .trim()
  }

  // Function to format currency
  const formatCurrency = (amount: string | number): string => {
    const numAmount = typeof amount === "string" ? Number.parseInt(amount) || 0 : amount
    return `₹${numAmount.toLocaleString("en-IN")}`
  }

  // Parse JSON response
  let itineraryData: ItineraryData | null = null

  try {
    const cleanedSuggestions = cleanText(suggestions)
    itineraryData = JSON.parse(cleanedSuggestions)
  } catch (error) {
    console.error("Failed to parse JSON:", error)
  }

  // Format date range for header
  const formatDateRange = () => {
    if (!tripData?.startDate || !tripData?.endDate) return ""

    const start = new Date(tripData.startDate)
    const end = new Date(tripData.endDate)

    const formatDate = (date: Date) => {
      const months = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ]
      return `${months[date.getMonth()]} ${date.getDate()}`
    }

    const startFormatted = formatDate(start)
    const endFormatted = formatDate(end)
    const year = start.getFullYear()

    if (start.getMonth() === end.getMonth()) {
      return `${startFormatted}-${end.getDate()}, ${year}`
    } else {
      return `${startFormatted} - ${endFormatted}, ${year}`
    }
  }

  // Get activity type icon
  const getActivityIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case "transport":
        return <Car className="h-4 w-4 text-blue-600" />
      case "sightseeing":
        return <Camera className="h-4 w-4 text-green-600" />
      case "food":
        return <Utensils className="h-4 w-4 text-orange-600" />
      case "shopping":
        return <ShoppingBag className="h-4 w-4 text-purple-600" />
      case "accommodation":
        return <Hotel className="h-4 w-4 text-red-600" />
      case "cultural":
        return <Star className="h-4 w-4 text-yellow-600" />
      default:
        return <MapPin className="h-4 w-4 text-gray-600" />
    }
  }

  // Render map button
  const renderMapButton = (location: string) => {
    return (
      <button
        onClick={() => {
          const query = encodeURIComponent(`${location}, Telangana, India`)
          window.open(`https://www.google.com/maps/search/${query}`, "_blank")
        }}
        className="inline-flex items-center gap-1 px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors"
        title={`Navigate to ${location}`}
      >
        <LocationPinIcon className="h-3 w-3" />
        View on Map
      </button>
    )
  }

  if (!itineraryData) {
    return (
      <div className="bg-white mx-2 sm:mx-0 rounded-lg overflow-hidden border border-gray-200 p-6">
        <div className="text-center">
          <div className="text-red-600 mb-4">
            <Info className="h-8 w-8 mx-auto" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Unable to Parse Response</h3>
          <p className="text-gray-600 mb-4">The AI response could not be formatted properly. Here's the raw content:</p>
          <div className="bg-gray-50 rounded-lg p-4 text-left">
            <pre className="whitespace-pre-wrap text-sm text-gray-800">{cleanText(suggestions)}</pre>
          </div>
        </div>
      </div>
    )
  }

  const { itinerary } = itineraryData

  return (
    <div className="bg-white mx-2 sm:mx-0 rounded-lg overflow-hidden border border-gray-200">
      {/* Header Section */}
      <div className="bg-gray-50 px-6 py-6 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-green-500 rounded-lg flex-shrink-0">
              <Star className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 leading-tight">Your Personalized Telangana Itinerary</h1>
              <p className="text-gray-600 mt-1">AI-crafted travel plan just for you</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button className="flex items-center gap-2 px-4 py-2 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50 transition-colors">
              <Download className="h-4 w-4" />
              Export PDF
            </button>
            <button className="flex items-center gap-2 px-4 py-2 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50 transition-colors">
              <Share2 className="h-4 w-4" />
              Share
            </button>
            <button className="flex items-center gap-2 px-4 py-2 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50 transition-colors">
              <Heart className="h-4 w-4" />
              Save
            </button>
          </div>
        </div>

        {/* Trip Summary Stats */}
        <div className="mt-6 grid grid-cols-4 gap-8 text-center">
          <div>
            <div className="text-4xl font-bold text-green-600">{itinerary.duration}</div>
            <div className="text-sm text-gray-600 mt-1">Days</div>
          </div>
          <div>
            <div className="text-4xl font-bold text-blue-600">{itinerary.travelers}</div>
            <div className="text-sm text-gray-600 mt-1">Travelers</div>
          </div>
          <div>
            <div className="text-4xl font-bold text-purple-600">{itinerary.locations.length}</div>
            <div className="text-sm text-gray-600 mt-1">Cities</div>
          </div>
          <div>
            <div className="text-4xl font-bold text-orange-600">AI</div>
            <div className="text-sm text-gray-600 mt-1">Powered</div>
          </div>
        </div>
      </div>

      {/* Main Itinerary Header */}
      <div className="bg-gray-600 text-white px-6 py-4">
        <div className="flex items-center gap-3">
          <MapPin className="h-5 w-5" />
          <h2 className="text-lg font-semibold">
            # Telangana Travel Itinerary: {itinerary.locations.join(" & ")} ({formatDateRange()})
          </h2>
        </div>
      </div>

      {/* Budget Overview Table */}
      <div className="px-6 py-6 border-b border-gray-200">
        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <DollarSign className="h-5 w-5 text-green-600" />
          Budget Overview
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full border border-gray-200 rounded-lg">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">Category</th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-gray-900 border-b">Cost</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 text-left">
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm text-gray-900">Accommodation</td>
                <td className="px-4 py-3 text-sm text-gray-900 text-right font-medium">
                  {formatCurrency(itinerary.budget.accommodation)}
                </td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm text-gray-900">Food & Dining</td>
                <td className="px-4 py-3 text-sm text-gray-900 text-right font-medium">
                  {formatCurrency(itinerary.budget.food)}
                </td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm text-gray-900">Transportation</td>
                <td className="px-4 py-3 text-sm text-gray-900 text-right font-medium">
                  {formatCurrency(itinerary.budget.transport)}
                </td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm text-gray-900">Attractions & Activities</td>
                <td className="px-4 py-3 text-sm text-gray-900 text-right font-medium">
                  {formatCurrency(itinerary.budget.attractions)}
                </td>
              </tr>
              <tr className="bg-green-50 font-semibold">
                <td className="px-4 py-3 text-sm text-green-900">Total Estimated Cost</td>
                <td className="px-4 py-3 text-sm text-green-900 text-right font-bold">
                  {formatCurrency(itinerary.budget.total)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Day-by-Day Schedule */}
      <div className="px-6 py-6">
        <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
          <Calendar className="h-5 w-5 text-blue-600" />
          Day-by-Day Schedule
        </h3>

        <div className="space-y-8">
          {itinerary.days.map((day, dayIndex) => (
            <div key={dayIndex} className="border border-gray-200 rounded-lg overflow-hidden">
              {/* Day Header */}
              <div className="bg-blue-600 text-white px-6 py-4">
                <h4 className="text-xl font-bold">
                  Day {day.dayNumber} ({cleanText(day.date)}): {cleanText(day.title)}
                </h4>
                <p className="text-blue-100 mt-1">📍 {cleanText(day.city)}</p>
              </div>

              {/* Activities Table */}
              <div className="p-6">
                <h5 className="text-lg font-semibold text-gray-900 mb-4">Activities Schedule</h5>
                <div className="overflow-x-auto">
                  <table className="w-full border border-gray-200 rounded-lg overflow-hidden">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">Time</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">Activity</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">Location</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">Type</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">Cost</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">Duration</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 text-left whitespace-nowrap">
                      {day.schedule.map((schedule, scheduleIndex) => (
                        <React.Fragment key={scheduleIndex}>
                          {/* Time Period Header */}
                          <tr className="bg-blue-50">
                            <td colSpan={7} className="px-4 py-2 text-sm font-bold text-blue-900">
                              {cleanText(schedule.period)} ({cleanText(schedule.timeSlot)})
                            </td>
                          </tr>
                          {/* Activities */}
                          {schedule.activities.map((activity, activityIndex) => (
                            <tr key={activityIndex} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                                {cleanText(activity.time)}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                <div>
                                  <div className="font-medium">{cleanText(activity.activity)}</div>
                                  {activity.tips && (
                                    <div className="text-xs text-gray-600 mt-1">💡 {cleanText(activity.tips)}</div>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">{cleanText(activity.location)}</td>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                <div className="flex items-center gap-2">
                                  {getActivityIcon(activity.type)}
                                  <span>{cleanText(activity.type)}</span>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                                {formatCurrency(activity.cost)}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">{cleanText(activity.duration)}</td>
                              <td className="px-4 py-3 text-sm">{renderMapButton(cleanText(activity.location))}</td>
                            </tr>
                          ))}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Meals Table */}
                {day.meals && day.meals.length > 0 && (
                  <div className="mt-6 text-left whitespace-nowrap">
                    <h5 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Utensils className="h-5 w-5 text-orange-600" />
                      Dining Recommendations
                    </h5>
                    <div className="overflow-x-auto">
                      <table className="w-full border border-gray-200 rounded-lg">
                        <thead className="bg-orange-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">Meal</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">
                              Restaurant
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">
                              Location
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">
                              Cuisine
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">
                              Speciality
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">Cost</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">Timing</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">Action</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {day.meals.map((meal, mealIndex) => (
                            <tr key={mealIndex} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm font-medium text-gray-900">{cleanText(meal.type)}</td>
                              <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                                {cleanText(meal.restaurant)}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">{cleanText(meal.location)}</td>
                              <td className="px-4 py-3 text-sm text-gray-900">{cleanText(meal.cuisine)}</td>
                              <td className="px-4 py-3 text-sm text-gray-900">{cleanText(meal.speciality)}</td>
                              <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                                {formatCurrency(meal.cost)}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">{cleanText(meal.timing)}</td>
                              <td className="px-4 py-3 text-sm">
                                {renderMapButton(cleanText(meal.restaurant + " " + meal.location))}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Accommodation Table */}
                {day.accommodation && (
                  <div className="mt-6 text-left whitespace-nowrap">
                    <h5 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Hotel className="h-5 w-5 text-red-600" />
                      Accommodation
                    </h5>
                    <div className="overflow-x-auto">
                      <table className="w-full border border-gray-200 rounded-lg">
                        <thead className="bg-red-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">
                              Hotel Name
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">
                              Location
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">Type</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">Rating</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">
                              Cost/Night
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">
                              Amenities
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">Action</th>
                          </tr>
                        </thead>
                        <tbody className="whitespace-nowrap">
                          <tr className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                              {cleanText(day.accommodation.name)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">{cleanText(day.accommodation.location)}</td>
                            <td className="px-4 py-3 text-sm text-gray-900">{cleanText(day.accommodation.type)}</td>
                            <td className="px-4 py-3 text-sm text-gray-900">{cleanText(day.accommodation.rating)}</td>
                            <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                              {formatCurrency(day.accommodation.cost)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              <div className="flex gap-1">
                                {day.accommodation.amenities.map((amenity, index) => (
                                  <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                                    {cleanText(amenity)}
                                  </span>
                                ))}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {renderMapButton(cleanText(day.accommodation.name + " " + day.accommodation.location))}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Essential Information */}
      <div className="px-6 py-6 border-t border-gray-200 text-left">
        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Info className="h-5 w-5 text-blue-600" />
          Essential Travel Information
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full border border-gray-200 rounded-lg">
            <thead className="bg-blue-50 whitespace-nowrap">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">Category</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">Information</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 whitespace-nowrap">
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm font-medium text-gray-900">Best Time to Visit</td>
                <td className="px-4 py-3 text-sm text-gray-900">{cleanText(itinerary.essentials.bestTime)}</td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm font-medium text-gray-900">Weather</td>
                <td className="px-4 py-3 text-sm text-gray-900">{cleanText(itinerary.essentials.weather)}</td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm font-medium text-gray-900">Recommended Clothing</td>
                <td className="px-4 py-3 text-sm text-gray-900">{cleanText(itinerary.essentials.clothing)}</td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm font-medium text-gray-900">Languages</td>
                <td className="px-4 py-3 text-sm text-gray-900">{cleanText(itinerary.essentials.language)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="px-6 py-6 border-t border-gray-200">
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
          <button className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-4 py-3 rounded-md font-medium transition-all duration-200 text-sm sm:text-base">
            <Calendar className="h-4 w-4 mr-2 inline" />
            Add to Calendar
          </button>
          <button className="flex-1 bg-transparent border border-gray-300 hover:bg-gray-50 px-4 py-3 rounded-md font-medium transition-all duration-200 text-sm sm:text-base">
            <Users className="h-4 w-4 mr-2 inline" />
            Share with Group
          </button>
          <button className="flex-1 bg-transparent border border-gray-300 hover:bg-gray-50 px-4 py-3 rounded-md font-medium transition-all duration-200 text-sm sm:text-base">
            <LocationPinIcon className="h-4 w-4 mr-2 inline" />
            View on Map
          </button>
        </div>
      </div>
    </div>
  )
}

export default TravelPlanDisplay;
